import { useEffect, useRef, useState } from "react";
import { ChevronDown, Search, ShoppingBag } from "lucide-react";
import { productImage } from "@/lib/api";
import { Input } from "@/components/ui/input";

/** Compact searchable product dropdown — the list never expands the form. */
export default function ProductCombobox({ products, onPick, placeholder = "Search or select product…", testid = "product-combobox" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const nq = q.trim().toLowerCase();
  const results = (products || []).filter((p) => !nq || p.name.toLowerCase().includes(nq));

  const pick = (p) => {
    onPick(p);
    setQ("");
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef} data-testid={testid}>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
        <Input
          data-testid={`${testid}-input`}
          className="h-11 pl-9 pr-9"
          placeholder={placeholder}
          value={q}
          onFocus={() => setOpen(true)}
          onClick={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
        />
        <ChevronDown className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>
      {open && (
        <div
          data-testid={`${testid}-list`}
          className="absolute z-50 mt-1 max-h-60 w-full overflow-y-auto overscroll-contain rounded-md border bg-card shadow-lg"
        >
          {results.length === 0 ? (
            <p className="px-3 py-3 text-xs text-muted-foreground" data-testid={`${testid}-empty`}>
              {(products || []).length === 0 ? "No products in this batch yet" : "No product matches your search"}
            </p>
          ) : (
            results.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`product-pick-${p.id}`}
                onClick={() => pick(p)}
                className="flex w-full items-center gap-2.5 border-b px-3 py-2.5 text-left last:border-b-0 hover:bg-accent/60 active:scale-[0.99]"
              >
                {productImage(p) ? (
                  <img src={productImage(p)} alt="" className="h-8 w-8 shrink-0 rounded-md border bg-stone-100 object-cover" />
                ) : (
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-stone-100">
                    <ShoppingBag className="h-4 w-4 text-stone-300" />
                  </span>
                )}
                <span className="min-w-0 flex-1 truncate text-sm font-semibold">{p.name}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
